/**
 * Bogue 1200 (Éric 2026-06-25) — File « à valider ».
 *
 * Les termes fraîchement générés depuis une synthèse n'entrent plus
 * directement au dictionnaire : ils attendent ici. Pour chacun, trois
 * décisions — valider, FUSIONNER dans un terme existant (le vrai but : gérer
 * les doublons avant qu'ils polluent le dictionnaire), ou rejeter.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "../ui/sonner";
import { CheckCircle, XCircle, ArrowsMerge, SpinnerGap, X } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PendingTermsModal({ onClose, onChanged, lang = "fr", entries = [] }) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [mergeTarget, setMergeTarget] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/jargon/pending`, { withCredentials: true });
      setPending(data?.pending || []);
    } catch (e) {
      toast.error((lang === "fr" ? "Chargement impossible : " : "Load failed: ")
        + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [lang]);

  useEffect(() => { load(); }, [load]);

  const act = async (entry, action) => {
    setBusy(entry.id);
    try {
      const body = action === "merge" ? { target_id: mergeTarget[entry.id] } : {};
      if (action === "merge" && !body.target_id) {
        toast.error(lang === "fr" ? "Choisis d'abord le terme à conserver." : "Pick the term to keep first.");
        return;
      }
      const { data } = await axios.post(
        `${API}/api/jargon/pending/${entry.id}/${action}`, body, { withCredentials: true },
      );
      toast.success(
        action === "approve"
          ? (lang === "fr" ? `« ${entry.term} » est entré au dictionnaire.` : `"${entry.term}" added.`)
          : action === "merge"
            ? (lang === "fr" ? `« ${entry.term} » fusionné dans « ${data.merged_into} ».` : `Merged into "${data.merged_into}".`)
            : (lang === "fr" ? `« ${entry.term} » rejeté.` : `"${entry.term}" rejected.`),
      );
      setPending((prev) => prev.filter((p) => p.id !== entry.id));
      if (onChanged) onChanged();
    } catch (e) {
      toast.error((lang === "fr" ? "Action impossible : " : "Action failed: ")
        + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="pending-terms-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-zinc-900">
              {lang === "fr" ? "Termes à valider" : "Terms awaiting validation"}
              <span className="ml-2 text-sm font-normal text-zinc-500" data-testid="pending-terms-count">
                {pending.length}
              </span>
            </h3>
            <p className="text-xs text-zinc-500 mt-0.5">
              {lang === "fr"
                ? "Ces termes viennent d'être générés depuis une synthèse. Ils n'entreront au dictionnaire qu'après ta décision."
                : "Freshly generated from a synthesis. They only enter the dictionary once you decide."}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-100 rounded" data-testid="pending-terms-close">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <SpinnerGap size={16} className="animate-spin" />
              {lang === "fr" ? "Chargement…" : "Loading…"}
            </div>
          ) : pending.length === 0 ? (
            <p className="text-sm text-zinc-500" data-testid="pending-terms-empty">
              {lang === "fr"
                ? "Aucun terme en attente — le dictionnaire est à jour."
                : "Nothing pending — the dictionary is up to date."}
            </p>
          ) : (
            pending.map((entry) => (
              <div
                key={entry.id}
                className="border border-zinc-200 rounded-lg p-3"
                data-testid={`pending-term-${entry.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-zinc-900 break-words">{entry.term}</p>
                    <p className="text-xs text-zinc-600 mt-1 break-words">{entry.definition}</p>
                    {entry.duplicate_of_term && (
                      <p
                        className="text-[11px] text-amber-800 mt-1.5"
                        data-testid={`pending-term-duplicate-${entry.id}`}
                      >
                        {lang === "fr"
                          ? `Voisin déjà au dictionnaire : « ${entry.duplicate_of_term} »`
                          : `Close neighbour already in dictionary: "${entry.duplicate_of_term}"`}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => act(entry, "approve")}
                      disabled={busy === entry.id}
                      className="px-2.5 py-1.5 rounded border border-teal-300 bg-teal-50 text-teal-800 text-xs font-medium hover:bg-teal-100 disabled:opacity-50 inline-flex items-center gap-1"
                      data-testid={`pending-approve-${entry.id}`}
                      title={lang === "fr" ? "Faire entrer ce terme au dictionnaire" : "Add to dictionary"}
                    >
                      <CheckCircle size={14} /> {lang === "fr" ? "Valider" : "Approve"}
                    </button>
                    <button
                      onClick={() => act(entry, "reject")}
                      disabled={busy === entry.id}
                      className="px-2.5 py-1.5 rounded border border-zinc-300 bg-white text-zinc-700 text-xs font-medium hover:bg-zinc-50 disabled:opacity-50 inline-flex items-center gap-1"
                      data-testid={`pending-reject-${entry.id}`}
                      title={lang === "fr" ? "Écarter ce terme" : "Reject"}
                    >
                      <XCircle size={14} /> {lang === "fr" ? "Rejeter" : "Reject"}
                    </button>
                  </div>
                </div>

                <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                  <select
                    value={mergeTarget[entry.id] || ""}
                    onChange={(e) => setMergeTarget((m) => ({ ...m, [entry.id]: e.target.value }))}
                    className="text-xs border border-zinc-300 rounded px-2 py-1.5 max-w-xs"
                    data-testid={`pending-merge-select-${entry.id}`}
                  >
                    <option value="">
                      {lang === "fr" ? "— fusionner dans… —" : "— merge into… —"}
                    </option>
                    {/* Tâche 1364 (Eric 2026-06-30) — Ordre alphabétique
                        insensible à la casse ET aux accents, la casse
                        d'origine restant affichée. Le tri vient déjà du
                        serveur (collation française) ; on le redit ici pour
                        que la garantie tienne même si cette liste est un jour
                        alimentée autrement. */}
                    {[...entries]
                      .sort((a, b) => String(a.term || "").localeCompare(
                        String(b.term || ""), "fr", { sensitivity: "base" }))
                      .map((c) => (
                        <option key={c.id} value={c.id}>{c.term}</option>
                      ))}
                  </select>
                  <button
                    onClick={() => act(entry, "merge")}
                    disabled={busy === entry.id || !mergeTarget[entry.id]}
                    className="px-2.5 py-1.5 rounded border border-violet-300 bg-violet-50 text-violet-800 text-xs font-medium hover:bg-violet-100 disabled:opacity-50 inline-flex items-center gap-1"
                    data-testid={`pending-merge-${entry.id}`}
                    title={lang === "fr"
                      ? "Le terme conservé hérite des sources et du titre du candidat en alias"
                      : "The kept term inherits sources and the candidate title as alias"}
                  >
                    <ArrowsMerge size={14} /> {lang === "fr" ? "Fusionner" : "Merge"}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
